/**
 * The consent notice, cut into SECTIONS for the read-aloud (SPEC_CAPTURE_UX_R2 §3.2).
 *
 * "Reads the notice text section by section, the same text the customer scrolls" — so the cut follows the
 * headings `NoticeBody` already renders (`# title`, `## heading`, `- item`, blank-line paragraphs). Each section
 * is the heading plus the prose under it, as ONE spoken string: the heading gives a listener the same landmark a
 * reader gets from the bold line.
 *
 * Nothing here is the consent record. The DPDP record is the notice TEXT and its version/sha256 (`SvNoticeCatalogue`
 * on the be); the reading is an accessibility AID and the "I agree and start" tap remains the only consent action.
 */

/** Markdown markers a listener cannot hear: `**bold**`, the leading `- ` of a list item. */
export function speakable(line: string): string {
  return line.replace(/\*\*/g, '').replace(/^-\s+/, '').trim();
}

export interface NoticeSection {
  /** 0-based position in the notice — the cue key and the progress line both use it. */
  readonly index: number;
  /** The heading, when the section had one (spoken first, then the prose). */
  readonly heading: string | null;
  /** Heading + prose, one string, ready to speak. */
  readonly text: string;
}

/** A section shorter than this is folded into the next one — a one-word heading is not a section on its own. */
const MIN_CHARS = 24;

interface Draft {
  heading: string | null;
  parts: string[];
  /** Lines carried FORWARD from a too-short section (a bare title), spoken before this section's heading. */
  lead: string[];
}

export function noticeSections(body: string): readonly NoticeSection[] {
  const out: Draft[] = [];
  const open = (heading: string | null): Draft => {
    const draft: Draft = { heading, parts: [], lead: [] };
    out.push(draft);
    return draft;
  };
  for (const raw of (body ?? '').split(/\r?\n/)) {
    const line = raw.trim();
    if (line === '') continue;
    if (line.startsWith('# ') || line.startsWith('## ')) {
      open(speakable(line.replace(/^#{1,2}\s+/, '')));
      continue;
    }
    const current = out[out.length - 1] ?? open(null);
    current.parts.push(speakable(line));
  }
  // A heading with nothing (or almost nothing) under it — a bare notice title — is not a section of its own: it is
  // carried FORWARD and spoken as the opening line of the next real section.
  const folded: Draft[] = [];
  let carry: string[] = [];
  const nonEmpty = (p: string | null): p is string => !!p && p.trim() !== '';
  for (const section of out) {
    const chars = [section.heading ?? '', ...section.parts].join(' ').trim().length;
    if (chars < MIN_CHARS) {
      carry.push(...[section.heading, ...section.parts].filter(nonEmpty));
      continue;
    }
    folded.push({ heading: section.heading, parts: [...section.parts], lead: carry });
    carry = [];
  }
  const last = folded[folded.length - 1];
  if (carry.length > 0 && last) last.parts.push(...carry);
  else if (carry.length > 0) folded.push({ heading: null, parts: carry, lead: [] });
  return folded
    .map((section) => ({
      heading: section.heading,
      text: [...section.lead, section.heading, section.parts.join(' ')]
        .filter(nonEmpty)
        .join('. ')
        .replace(/\.\.\s/g, '. ')
        .trim(),
    }))
    .filter((section) => section.text !== '')
    .map((section, index) => ({ index, heading: section.heading, text: section.text }));
}
